/**
 *  @flow
 */

import React, { useState, useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { map, get } from 'lodash';
import { Icon, Message } from 'semantic-ui-react';

import { Modal, Button } from '../../../../components';
import { prepareFloatForServer } from '../../../../lib/numberFormatter';
import CartForm from './CartForm';

export const SELL_PRODUCTS_MUTATION = gql`
    mutation createCartInvoice($input: CreateCartInvoiceInput!) {
        createCartInvoice(input: $input) {
            invoice {
                id
                paid
            }
        }
    }
`;

export const ARCHIVE_PRODUCTS_MUTATION = gql`
    mutation archiveCartInvoice($input: ArchiveCartInvoiceInput!) {
        archiveCartInvoice(input: $input) {
            invoice {
                id
            }
        }
    }
`;

const intlMessages = defineMessages({
    productQuantityError: {
        id: 'pages.administrators.form.productQuantityError',
        defaultMessage: 'Invoice item quantity is too big',
    },
    InternalError: {
        id: 'pages.administrators.form.internalError',
        defaultMessage: 'Internal server error',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const DEFAULT_ITEM = {
    entity: null,
    quantity: null,
    price: null,
    paymentType: null,
};

const PaidMessage = () => (
    <Message info>
        <Icon name="info" />
        <FormattedMessage
            id="pages.appointments.cart.message.paid"
            defaultMessage="Submit success"
        />
    </Message>
);

const ErrorMessage = ({ error }) => (
    <Message error>
        <Icon name="warning sign" />
        {error}
    </Message>
);

type CartDialogProps = {
    trigger: any,
    open: boolean,
    onClose: Function,
    intl: Object,
};

function CartDialog({ trigger, open, onClose, intl }: CartDialogProps) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const administrator = useSelector(({ user }) => user.get('id'));
    const enableArchive = useSelector(
        ({ salon: s }) => s.get('settings').enableArchive,
    );
    const isSysadmin = useSelector(({ user }) => user.get('isSysadmin'));
    const isOwner = useSelector(({ user }) => user.get('isOwner'));

    const [paid, setPaid] = useState(false);
    const [invoice, setInvoice] = useState({});

    const methods = useForm({
        defaultValues: {
            administrator,
            items: [{ ...DEFAULT_ITEM }],
        },
    });
    const {
        handleSubmit,
        reset,
        setError,
        formState: { isSubmitting, errors },
    } = methods;

    useEffect(() => {
        if (open) {
            setPaid(false);
            setInvoice({});
            reset({
                administrator,
                items: [{ ...DEFAULT_ITEM }],
            });
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const [sellProducts] = useMutation(SELL_PRODUCTS_MUTATION);
    const [archiveProducts] = useMutation(ARCHIVE_PRODUCTS_MUTATION);

    const onSubmit = async formData => {
        const items = map(formData.items, item =>
            Object.assign({}, item, {
                type: 'PRODUCT',
                seller: formData.administrator,
                price: prepareFloatForServer(item.price),
            }),
        );

        try {
            const { data } = await sellProducts({
                variables: {
                    input: {
                        salon,
                        administrator: formData.administrator,
                        items,
                    },
                },
            });
            setInvoice(data.createCartInvoice.invoice);
            setPaid(true);
        } catch (error) {
            if (error.graphQLErrors && error.graphQLErrors.length) {
                const graphQLError = error.graphQLErrors[0];
                if (
                    graphQLError.name === 'IllegalArgument' &&
                    get(graphQLError, 'data.error') === 'NEGATIVE_PRODUCT_QUANTITY'
                ) {
                    setError('root', {
                        message: intl.formatMessage(
                            intlMessages.productQuantityError,
                        ),
                    });
                    return;
                } else if (
                    graphQLError.name === 'InternalError' &&
                    get(graphQLError, 'data.error') === 'INTERNAL_SERVER_ERROR'
                ) {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.InternalError),
                    });
                    return;
                }
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    const onArchive = async () => {
        await archiveProducts({
            variables: {
                input: { invoiceId: invoice.id, archived: true },
            },
        });
        onClose();
    };

    const rootError = errors.root && errors.root.message;

    return (
        <FormProvider {...methods}>
            <Modal size="small" trigger={trigger} onClose={onClose} open={open}>
                <Modal.Header>
                    <FormattedMessage
                        id="pages.appointments.cart.form.header"
                        defaultMessage="Sale"
                    />
                </Modal.Header>
                <Modal.Content>
                    {rootError && <ErrorMessage error={rootError} />}
                    {paid && <PaidMessage />}
                    <CartForm submitting={isSubmitting} paid={paid} />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || paid}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.appointments.cart.button.submit"
                            defaultMessage="Submit"
                        />
                    </Button>
                    <Button onClick={onClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.appointments.cart.button.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                    {(enableArchive || isSysadmin || isOwner) && paid && (
                        <Button
                            onClick={onArchive}
                            disabled={!paid}
                            color="orange"
                            floated="right"
                        >
                            <Icon name="archive" />
                            <FormattedMessage
                                id="pages.appointments.details.ArchiveButton.archive"
                                defaultMessage="Archive"
                            />
                        </Button>
                    )}
                </Modal.Actions>
            </Modal>
        </FormProvider>
    );
}

export default injectIntl(CartDialog);
